/*
 * Drives the visible オプション checkboxes on the product page.
 *
 * The theme's variant machinery (VariantSelects in global.js, product-info.js)
 * reads option state from native <select> elements. Those selects are kept
 * hidden inside <variant-selects>, which product-info.js replaces wholesale on
 * every variant change. Anything the customer typed inside that element would
 * be lost, so every visible control lives outside it and writes through to the
 * hidden select, dispatching the bubbling `change` that VariantSelects listens
 * for.
 *
 * A field that must not reach the cart has its `name` removed rather than only
 * being disabled: the dynamic checkout button serialises the form without
 * honouring `disabled`, so a disabled-but-named field still lands on the order.
 */
if (!customElements.get('product-options')) {
  class ProductOptions extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.toggles = Array.from(this.querySelectorAll('[data-option-toggle]'));
      this.nameplateToggle = this.querySelector('[data-nameplate-toggle]');
      this.nameplateSelect = this.querySelector('[data-nameplate-select]');
      this.nameplateInput = this.querySelector('[data-nameplate-input]');
      this.nameplateAlert = this.querySelector('[data-nameplate-alert]');

      if (this.nameplateInput) {
        this.nameplateInput.dataset.propertyName = this.nameplateInput.getAttribute('name');
      }

      this.toggles.forEach((toggle) => {
        toggle.addEventListener('change', () => this.handleToggle(toggle));
      });

      if (this.nameplateSelect) {
        this.nameplateSelect.addEventListener('change', () => {
          this.writeOption(this.nameplateSelect.dataset.optionName, this.nameplateSelect.value);
          this.syncNameplate();
        });
      }

      if (this.nameplateInput) {
        this.nameplateInput.addEventListener('input', () => this.syncNameplate());
      }

      // Clear a field's error as soon as the customer starts fixing it.
      this.requiredFields.forEach((field) => {
        field.addEventListener('input', () => this.clearError(field));
      });

      this.form = document.getElementById(this.dataset.productFormId);
      this.handleSubmit = (event) => {
        if (event.target !== this.form) return;
        if (this.reportMissing()) {
          event.preventDefault();
          // Capture phase on the document: stopping here means the event never
          // reaches product-form.js's own submit handler on the form.
          event.stopPropagation();
        }
      };
      // Capture on the document, not the form: a capture listener on the form
      // itself would still fire at target phase, in registration order, and
      // product-form.js may well have registered first.
      document.addEventListener('submit', this.handleSubmit, true);

      // The dynamic checkout button posts the form without ever firing submit.
      this.paymentButton = this.form?.querySelector('.shopify-payment-button');
      this.handlePaymentClick = (event) => {
        if (this.reportMissing()) {
          event.preventDefault();
          event.stopPropagation();
        }
      };
      this.paymentButton?.addEventListener('click', this.handlePaymentClick, true);

      this.syncFromVariant();

      // Returning via the back button restores checkbox state without firing
      // `change`, which would leave a toggle on and its panel shut.
      this.handlePageShow = () => this.syncFromVariant();
      window.addEventListener('pageshow', this.handlePageShow);
    }

    disconnectedCallback() {
      window.removeEventListener('pageshow', this.handlePageShow);
      document.removeEventListener('submit', this.handleSubmit, true);
      this.paymentButton?.removeEventListener('click', this.handlePaymentClick, true);
    }

    /* A field is required only while its panel is open. Both this element and
       gift-options.js disable the fields of a shut panel, so `disabled` is the
       single signal for "not currently asked for". */
    get requiredFields() {
      return Array.from(this.querySelectorAll('[data-required-when-open]'));
    }

    errorSlot(field) {
      return this.querySelector(`[data-error-for="${CSS.escape(field.id)}"]`);
    }

    showError(field, message) {
      field.setAttribute('aria-invalid', 'true');
      const slot = this.errorSlot(field);
      if (slot) {
        slot.textContent = message;
        slot.hidden = false;
      }
    }

    clearError(field) {
      const slot = this.errorSlot(field);
      if (slot) slot.hidden = true;
      // The nameplate also turns red for the cheaper-tier advisory, so hand
      // that state back rather than clearing aria-invalid outright.
      if (field === this.nameplateInput) this.syncNameplate();
      else field.setAttribute('aria-invalid', 'false');
    }

    /* Flags every open-but-empty field, then sends the customer to the first
       one on the page. Returns true when the submit should be blocked. */
    reportMissing() {
      const missing = this.requiredFields.filter(
        (field) => !field.disabled && field.value.trim() === ''
      );

      this.requiredFields
        .filter((field) => !missing.includes(field))
        .forEach((field) => this.clearError(field));

      if (missing.length === 0) return false;

      missing.forEach((field) => this.showError(field, field.dataset.errorMessage));

      // Topmost by rendered position, not DOM order — panels open and close.
      const first = missing.reduce((top, field) =>
        field.getBoundingClientRect().top < top.getBoundingClientRect().top ? field : top
      );
      first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      first.focus({ preventScroll: true });

      return true;
    }

    /* The hidden selects are re-rendered on every variant change, so they are
       looked up on each use rather than cached. */
    nativeSelect(optionName) {
      const host = document.getElementById(`variant-selects-${this.sectionId}`);
      if (!host || !optionName) return null;
      return host.querySelector(`select[data-option-name="${CSS.escape(optionName)}"]`);
    }

    writeOption(optionName, value) {
      const select = this.nativeSelect(optionName);
      if (!select || select.value === value) return;

      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    panelFor(toggle) {
      const id = toggle.getAttribute('aria-controls');
      return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    }

    handleToggle(toggle) {
      const { optionToggle: optionName, onValue, offValue } = toggle.dataset;
      const panel = this.panelFor(toggle);

      if (panel) panel.hidden = !toggle.checked;
      toggle.setAttribute('aria-expanded', String(toggle.checked));

      // Re-opening resets to the cheapest paid tier, per the spec.
      if (toggle === this.nameplateToggle && this.nameplateSelect && toggle.checked) {
        this.nameplateSelect.value = onValue;
      }

      this.writeOption(optionName, toggle.checked ? onValue : offValue);

      if (toggle === this.nameplateToggle) this.syncNameplate();
    }

    /* Derives the visible state from the hidden selects, so a ?variant= deep
       link or a bfcache restore shows the right boxes ticked. */
    syncFromVariant() {
      this.toggles.forEach((toggle) => {
        const { optionToggle: optionName, offValue } = toggle.dataset;
        const select = this.nativeSelect(optionName);
        if (!select) return;

        toggle.checked = select.value !== offValue;
        toggle.setAttribute('aria-expanded', String(toggle.checked));

        const panel = this.panelFor(toggle);
        if (panel) panel.hidden = !toggle.checked;

        if (toggle === this.nameplateToggle && this.nameplateSelect && toggle.checked) {
          this.nameplateSelect.value = select.value;
        }
      });

      this.syncNameplate();
    }

    /* Character tiers offered by the select, e.g. "2文字(+1.100円)" -> 2. */
    get nameplateTiers() {
      if (!this.nameplateSelect) return [];
      return Array.from(this.nameplateSelect.options)
        .map((option) => ({ value: option.value, limit: parseInt(option.value, 10) }))
        .filter(({ limit }) => Number.isInteger(limit));
    }

    syncNameplate() {
      const input = this.nameplateInput;
      if (!input) return;

      const active = !!this.nameplateToggle?.checked;
      const tiers = this.nameplateTiers;
      const current = tiers.find(({ value }) => value === this.nameplateSelect?.value);
      const limit = current ? current.limit : 0;

      input.disabled = !active;
      if (active && limit) {
        input.maxLength = limit;
      } else {
        input.removeAttribute('maxlength');
      }

      const typed = input.value.trim();
      if (active && typed !== '') {
        input.setAttribute('name', input.dataset.propertyName);
      } else {
        input.removeAttribute('name');
      }

      // A name that would fit a cheaper tier is flagged rather than blocked —
      // the customer may still want the wider spacing they paid for.
      const cheaper = tiers.find(({ limit: tier }) => tier < limit && typed.length <= tier);
      const showAlert = active && typed !== '' && !!cheaper;

      const message = showAlert
        ? `${cheaper.limit}文字以内の場合は、${cheaper.limit}文字オプションをお選びください`
        : '';

      if (this.nameplateAlert) {
        this.nameplateAlert.hidden = !showAlert;
        if (showAlert) this.nameplateAlert.textContent = message;
      }
      input.setAttribute('aria-invalid', String(showAlert));

      // The empty-name case is handled by reportMissing() on submit, so that it
      // can scroll to the field and render the message inline rather than in a
      // native validation bubble.
    }
  }

  customElements.define('product-options', ProductOptions);
}
